import { cn } from "@/lib/cn";
import { Pill } from "./kit";
import type { DomainStory as Story } from "@/content/home";

const BEAT_LABELS: { key: "model" | "simulate" | "generate"; label: string }[] = [
  { key: "model", label: "Model" },
  { key: "simulate", label: "Simulate" },
  { key: "generate", label: "Generate" },
];

/**
 * One domain story row: the illustration on one side, the model->simulate->
 * generate beats on the other. `flip` swaps sides for alternating rhythm.
 * The dark/gold art is framed in the light card system per the design spec.
 */
export function DomainStory({ story, flip = false }: { story: Story; flip?: boolean }) {
  return (
    <div className="grid items-center gap-8 md:grid-cols-2 md:gap-12">
      {/* Visual */}
      <figure
        className={cn(
          "overflow-hidden rounded-2xl border border-border bg-card shadow-lift",
          flip && "md:order-2",
        )}
      >
        <img
          src={story.image}
          alt={story.imageAlt}
          className="block aspect-[3/2] w-full object-cover"
          loading="lazy"
        />
      </figure>

      {/* Text + beats */}
      <div className={cn("flex flex-col gap-5", flip && "md:order-1")}>
        <div className="flex flex-col gap-3">
          <div>
            <Pill tone={story.tone}>{story.domain}</Pill>
          </div>
          <h3 className="text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl">
            {story.system}
          </h3>
          <p className="text-base leading-relaxed text-muted-foreground">{story.blurb}</p>
        </div>

        <ol className="flex list-none flex-col gap-3">
          {BEAT_LABELS.map(({ key, label }) => (
            <li key={key} className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex shrink-0 items-center rounded-full bg-primary-tint px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-primary">
                {label}
              </span>
              <span className="text-sm leading-relaxed text-foreground">{story.beats[key]}</span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
